import { useCallback, useEffect, useRef, useState } from 'react';
import axios from 'axios';
import { toast } from 'react-toastify';
import { useSearchParams } from 'react-router-dom';
import { Alert, Badge, Button, Card, Form, InputGroup, Table } from 'react-bootstrap';
import { formatDate } from '../utils/dateFormat';

const LEVEL_VARIANT = { ERROR: 'danger', WARN: 'warning', INFO: 'secondary', DEBUG: 'light', TRACE: 'light' };

const timeOf = (ts) => {
  const d = new Date(ts);
  if (Number.isNaN(d.getTime())) return ts || '';
  return `${formatDate(d)} ${d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', second: '2-digit', hour12: false })}`;
};

const shortLogger = (name) => (name && name.includes('.') ? name.split('.').slice(-2).join('.') : name || '');

/** The result as plain text, to paste into a ticket reply or a message. */
const asText = (result) =>
  [`Ref: ${result.ref}`, ...result.lines.map((l) => `${timeOf(l.ts)} ${l.level} ${l.logger}: ${l.message}${l.stackTrace ? `\n${l.stackTrace}` : ''}`)].join('\n');

/**
 * Tech Support > Find by Ref: paste the Ref a customer copied from an error message ("Ref: 3fa9c01b2e") and see every line the server
 * logged for that request: what was asked, who and which organization it was for, what went wrong. One exact id at a time, never a
 * browse of the logs.
 */
export default function FindByRef() {
  const [params, setParams] = useSearchParams();
  const [text, setText] = useState(params.get('ref') || '');
  const [result, setResult] = useState(null);
  const [busy, setBusy] = useState(false);
  const [problem, setProblem] = useState('');
  const ranFromAddress = useRef(false);

  const search = useCallback(async (value) => {
    setBusy(true);
    setProblem('');
    try {
      const response = await axios.get('/api/system-admin/logs/by-ref', { params: { ref: value } });
      setResult(response.data);
    } catch (e) {
      setResult(null);
      setProblem(e.response?.data?.message || 'The search did not work. Try again.');
    } finally {
      setBusy(false);
    }
  }, []);

  // A link such as /tech-support/find-by-ref?ref=3fa9c01b2e searches at once
  useEffect(() => {
    const fromAddress = params.get('ref');
    if (fromAddress && !ranFromAddress.current) {
      ranFromAddress.current = true;
      search(fromAddress);
    }
  }, [params, search]);

  const submit = (e) => {
    e.preventDefault();
    if (!text.trim()) return;
    if (params.get('ref')) setParams({}, { replace: true });
    search(text);
  };

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(asText(result));
      toast.success('Copied.');
    } catch (e) {
      toast.info('Could not copy. Select the text and copy it instead.');
    }
  };

  const summary = result?.lines?.find((l) => l.logger === 'request');
  const who = result?.lines?.find((l) => l.user || l.org);

  return (
    <div data-testid="find-by-ref">
      <Card className="mb-3">
        <Card.Body>
          <Form onSubmit={submit}>
            <Form.Label htmlFor="ref-input">The Ref from the customer&apos;s error message</Form.Label>
            <InputGroup>
              <Form.Control
                id="ref-input"
                value={text}
                onChange={(e) => setText(e.target.value)}
                placeholder="Ref: 3fa9c01b2e (or paste the whole message)"
                autoComplete="off"
                spellCheck={false}
                autoFocus
              />
              <Button type="submit" disabled={busy || !text.trim()}>{busy ? 'Searching…' : 'Find'}</Button>
            </InputGroup>
            <Form.Text muted>Searches today&apos;s log and the last two weeks. One request at a time.</Form.Text>
          </Form>
        </Card.Body>
      </Card>

      {problem && <Alert variant="warning">{problem}</Alert>}

      {result && !result.logsFound && (
        <Alert variant="info">The server has no log files to search yet.</Alert>
      )}
      {result && result.logsFound && result.lines.length === 0 && (
        <Alert variant="info">
          {result.timedOut
            ? 'The search ran out of time before it found this Ref. Try again in a minute.'
            : `Nothing for Ref ${result.ref}. It may be older than the files kept (two weeks), or the error came from the customer's browser without reaching the server.`}
        </Alert>
      )}

      {result && result.lines.length > 0 && (
        <Card>
          <Card.Header className="d-flex flex-wrap justify-content-between align-items-center gap-2">
            <div>
              <strong>Ref {result.ref}</strong>
              {summary && <span className="ms-2 font-monospace small">{summary.message}</span>}
              {who && (
                <div className="small text-muted">
                  {who.user && <>Signed in as {who.user}</>}{who.user && who.org && ' · '}{who.org && <>Organization {who.org}</>}
                </div>
              )}
            </div>
            <Button size="sm" variant="outline-secondary" onClick={copy}><i className="bi bi-clipboard me-1" aria-hidden="true" />Copy</Button>
          </Card.Header>
          <Table responsive className="mb-0 align-middle" data-testid="ref-lines">
            <thead><tr><th>Time</th><th>Level</th><th>From</th><th>What</th></tr></thead>
            <tbody>
              {result.lines.map((l, i) => (
                <tr key={`${l.ts}-${i}`}>
                  <td className="text-nowrap small">{timeOf(l.ts)}</td>
                  <td><Badge bg={LEVEL_VARIANT[l.level] || 'secondary'} text={l.level === 'DEBUG' || l.level === 'TRACE' ? 'dark' : undefined}>{l.level}</Badge></td>
                  <td className="small text-muted text-nowrap" title={l.logger}>{shortLogger(l.logger)}</td>
                  <td style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
                    {l.message}
                    {l.stackTrace && (
                      <details className="mt-1">
                        <summary className="small text-muted">Error details</summary>
                        <pre className="small mb-0" style={{ whiteSpace: 'pre-wrap' }}>{l.stackTrace}</pre>
                      </details>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </Table>
          {result.truncated && <Card.Footer className="small text-muted">More lines than can be shown here; the first {result.lines.length} are listed.</Card.Footer>}
        </Card>
      )}
    </div>
  );
}
